import React, { useState, useCallback } from 'react';
import { Modal, message } from 'antd';
import { finBillDataApi, FINBillData, BillDataSearchParams } from '../../../services/finBillDataApi';
import { STATUS_OPTIONS, BILL_TYPE_OPTIONS, dateToInput, fmt } from '../helpers';

interface SearchModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (id: number) => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ visible, onClose, onSelect }) => {
  const [params, setParams] = useState<BillDataSearchParams>({});
  const [results, setResults] = useState<FINBillData[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searching, setSearching] = useState(false);

  const handleSearch = useCallback(async (p = 1) => {
    setSearching(true);
    try {
      const res = await finBillDataApi.search({ ...params, page: p, size: 20 });
      setResults(res.data || []);
      setTotal(res.total);
      setPage(p);
    } catch {
      message.error('搜尋失敗');
    } finally {
      setSearching(false);
    }
  }, [params]);

  const handleSelect = useCallback((rec: FINBillData) => {
    onSelect(rec.bill_mid);
    onClose();
  }, [onSelect, onClose]);

  const totalPages = Math.ceil(total / 20);

  return (
    <Modal
      title="票據搜尋"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      {/* Search Form */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <label style={{ fontSize: 13, whiteSpace: 'nowrap' }}>支票號碼</label>
          <input
            type="text" style={{ width: 120 }}
            value={params.check_no || ''}
            onChange={e => setParams(p => ({ ...p, check_no: e.target.value }))}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <label style={{ fontSize: 13, whiteSpace: 'nowrap' }}>票據狀態</label>
          <select
            value={params.status || ''}
            onChange={e => setParams(p => ({ ...p, status: e.target.value }))}
          >
            <option value="">全部</option>
            {STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <label style={{ fontSize: 13, whiteSpace: 'nowrap' }}>單據類別</label>
          <select
            value={params.bill_type || ''}
            onChange={e => setParams(p => ({ ...p, bill_type: e.target.value }))}
          >
            <option value="">全部</option>
            {BILL_TYPE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <label style={{ fontSize: 13, whiteSpace: 'nowrap' }}>受款人</label>
          <input
            type="text" style={{ width: 120 }}
            value={params.payee || ''}
            onChange={e => setParams(p => ({ ...p, payee: e.target.value }))}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <label style={{ fontSize: 13, whiteSpace: 'nowrap' }}>單據號碼</label>
          <input
            type="text" style={{ width: 120 }}
            value={params.bill_no || ''}
            onChange={e => setParams(p => ({ ...p, bill_no: e.target.value }))}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <label style={{ fontSize: 13, whiteSpace: 'nowrap' }}>開立日起</label>
          <input
            type="date"
            value={params.make_date_from || ''}
            onChange={e => setParams(p => ({ ...p, make_date_from: e.target.value }))}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <label style={{ fontSize: 13, whiteSpace: 'nowrap' }}>開立日迄</label>
          <input
            type="date"
            value={params.make_date_to || ''}
            onChange={e => setParams(p => ({ ...p, make_date_to: e.target.value }))}
          />
        </div>
        <button className="btn btn-primary" onClick={() => handleSearch(1)} disabled={searching}>
          {searching ? '搜尋中...' : '搜尋'}
        </button>
      </div>

      {/* Results Table */}
      {results.length > 0 && (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d9d9d9' }}>ID</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d9d9d9' }}>支票號碼</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d9d9d9' }}>類別</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d9d9d9' }}>受款人</th>
                <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #d9d9d9' }}>金額</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d9d9d9' }}>狀態</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d9d9d9' }}>到期日</th>
              </tr>
            </thead>
            <tbody>
              {results.map(r => (
                <tr
                  key={r.bill_mid}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSelect(r)}
                  onMouseEnter={e => (e.currentTarget.style.background = '#e6f7ff')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0' }}>{r.bill_mid}</td>
                  <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0' }}>{r.check_no}</td>
                  <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0' }}>{r.bill_type}</td>
                  <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0' }}>{r.payee}</td>
                  <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>{fmt(r.check_amt)}</td>
                  <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0' }}>{r.status}</td>
                  <td style={{ padding: '5px 8px', borderBottom: '1px solid #f0f0f0' }}>{dateToInput(r.expire_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 12, color: '#666' }}>
            <span>共 {total} 筆</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="btn btn-sm" disabled={page <= 1} onClick={() => handleSearch(page - 1)}>上一頁</button>
              <span style={{ lineHeight: '24px' }}>{page} / {totalPages}</span>
              <button className="btn btn-sm" disabled={page >= totalPages} onClick={() => handleSearch(page + 1)}>下一頁</button>
            </div>
          </div>
        </>
      )}
      {results.length === 0 && total === 0 && !searching && (
        <div style={{ textAlign: 'center', color: '#999', padding: 24 }}>
          輸入條件後按搜尋
        </div>
      )}
    </Modal>
  );
};

export default React.memo(SearchModal);
